import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';

import { CurrencyBadge } from '@/components/finance/CurrencyBadge.web';
import { Button, Text } from '@/components/ui';
import { distinctLabelColors } from '@/lib/design-system/label-color';
import { createCurrency, deleteCurrency, fetchCurrencies, updateCurrency } from '@/lib/finance/api';
import { STARTER_CURRENCIES, type Currency } from '@/lib/finance/types';

export function CurrenciesEditor() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newSymbol, setNewSymbol] = useState('');

  const currencyColors = useMemo(
    () => distinctLabelColors(currencies.map((c) => c.code)),
    [currencies],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCurrencies(await fetchCurrencies());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load currencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setBusy(false);
    }
  }

  async function onAdd() {
    const code = newCode.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      setError('Currency code must be 3 letters');
      return;
    }
    await run(async () => {
      await createCurrency({ code, symbol: newSymbol.trim() || null });
      setNewCode('');
      setNewSymbol('');
    });
  }

  async function onSeedStarters() {
    const existing = new Set(currencies.map((c) => c.code));
    const missing = STARTER_CURRENCIES.filter((c) => !existing.has(c.code));
    if (missing.length === 0) {
      return;
    }
    await run(async () => {
      for (const currency of missing) {
        await createCurrency({ code: currency.code, symbol: currency.symbol });
      }
    });
  }

  async function onDelete(code: string) {
    if (
      !window.confirm(`Delete currency ${code}? Accounts or transactions using it will block this.`)
    ) {
      return;
    }
    await run(() => deleteCurrency(code));
  }

  return (
    <View>
      <View className="mb-4 flex-row items-end gap-2">
        <View>
          <Text variant="caption" className="mb-1 text-muted">
            Code
          </Text>
          <TextInput
            autoCapitalize="characters"
            className="w-24 rounded-lg border border-border px-3 py-2 text-base uppercase"
            maxLength={3}
            onChangeText={setNewCode}
            placeholder="USD"
            value={newCode}
          />
        </View>
        <View>
          <Text variant="caption" className="mb-1 text-muted">
            Symbol
          </Text>
          <TextInput
            className="w-24 rounded-lg border border-border px-3 py-2 text-base"
            maxLength={8}
            onChangeText={setNewSymbol}
            placeholder="$"
            value={newSymbol}
          />
        </View>
        <Button
          label="Add"
          size="sm"
          variant="primary"
          disabled={busy}
          onPress={() => void onAdd()}
        />
        <View className="flex-1" />
        <Button
          label="Add starter set"
          size="sm"
          variant="secondary"
          disabled={busy}
          onPress={() => void onSeedStarters()}
        />
      </View>

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : currencies.length === 0 ? (
        <Text variant="body" className="text-muted">
          No currencies yet. Add the starter set to begin.
        </Text>
      ) : (
        <View className="rounded-lg border border-border">
          {currencies.map((currency, index) => (
            <View
              key={currency.code}
              className={`flex-row items-center gap-3 px-3 py-2 ${
                index > 0 ? 'border-t border-border' : ''
              }`}
            >
              <CurrencyBadge code={currency.code} color={currencyColors.get(currency.code)} />
              <input
                className="cs-input w-24"
                defaultValue={currency.symbol ?? ''}
                placeholder="symbol"
                onBlur={(e) => {
                  const next = e.target.value.trim() || null;
                  if (next !== (currency.symbol ?? null)) {
                    void run(() => updateCurrency(currency.code, next).then(() => undefined));
                  }
                }}
              />
              <View className="flex-1" />
              <Pressable onPress={() => void onDelete(currency.code)}>
                <Text variant="caption" className="text-danger">
                  Delete
                </Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
